import React, { useState, useEffect } from 'react';
import {
  Folder,
  FolderPlus,
  ChevronRight,
  ChevronDown,
  File,
  Trash2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger
} from '@/components/ui/collapsible';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { useAPIClient } from '@/hooks/useAPIClient';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/hooks/use-toast';

export interface FolderNode {
  name: string;
  path: string;
  full_path: string;
  file_count: number;
  children: FolderNode[];
}

export interface SelectedFolder {
  module: string;
  subpath?: string;
  display_path: string;
}

interface FolderSelectorProps {
  onFolderSelect: (folder: SelectedFolder) => void;
  selectedFolder?: SelectedFolder;
  className?: string;
}

export const FolderSelector: React.FC<FolderSelectorProps> = ({
  onFolderSelect,
  selectedFolder,
  className
}) => {
  const apiClient = useAPIClient();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set(['']));
  const [creatingFolder, setCreatingFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [newFolderParent, setNewFolderParent] = useState('');
  const [folderToDelete, setFolderToDelete] = useState<FolderNode | null>(null);

  // Fetch folder tree
  const { data: folderTree, isLoading } = useQuery({
    queryKey: ['upload-folder-tree'],
    queryFn: async () => {
      const response = await apiClient.request<FolderNode>('/api/upload/input-tree');
      return response;
    },
    staleTime: 30000, // 30 seconds
  });

  // Create folder mutation
  const createFolderMutation = useMutation({
    mutationFn: async ({ module, subpath }: { module: string; subpath?: string }) => {
      const response = await apiClient.request('/api/upload/create-folder', {
        method: 'POST',
        body: JSON.stringify({ module, subpath: subpath || '' }),
        headers: { 'Content-Type': 'application/json' }
      });
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['upload-folder-tree'] });
      setCreatingFolder(false);
      setNewFolderName('');
      setNewFolderParent('');
    },
  });

  // Delete folder mutation
  const deleteFolderMutation = useMutation({
    mutationFn: async (node: FolderNode) => {
      const pathParts = node.path ? node.path.split('/') : [];
      const module = pathParts.length > 0 ? pathParts[0] : node.name;
      const subpath = pathParts.length > 1 ? pathParts.slice(1).join('/') : undefined;

      return apiClient.upload.deleteFolder(module, subpath);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['upload-folder-tree'] });
      setFolderToDelete(null);
      toast({
        title: "Ordner gelöscht",
        description: "Der leere Ordner wurde erfolgreich entfernt.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Löschen fehlgeschlagen",
        description: error.message || "Der Ordner konnte nicht gelöscht werden.",
        variant: "destructive",
      });
    },
  });

  const toggleFolder = (path: string) => {
    const newExpanded = new Set(expandedFolders);
    if (newExpanded.has(path)) {
      newExpanded.delete(path);
    } else {
      newExpanded.add(path);
    }
    setExpandedFolders(newExpanded);
  };

  const handleFolderClick = (node: FolderNode) => {
    const pathParts = node.path ? node.path.split('/') : [];

    // For root level folders, the module is the node name
    // For nested folders, the first part of the path is the module
    const module = pathParts.length > 0 ? pathParts[0] : node.name;
    const subpath = pathParts.length > 1 ? pathParts.slice(1).join('/') : undefined;

    const folder: SelectedFolder = {
      module,
      subpath,
      display_path: node.path || node.name,
    };

    onFolderSelect(folder);
  };

  const handleCreateFolder = () => {
    if (!newFolderName.trim()) return;

    const parentParts = newFolderParent ? newFolderParent.split('/') : [];
    const module = parentParts[0] || newFolderName;
    const subpath = parentParts.length > 0 ?
      [...parentParts.slice(1), newFolderName].join('/') :
      undefined;

    createFolderMutation.mutate({ module, subpath });
  };

  const renderFolderNode = (node: FolderNode, level: number = 0): React.ReactNode => {
    const isExpanded = expandedFolders.has(node.path);
    const hasChildren = node.children && node.children.length > 0;
    const isSelected = selectedFolder?.display_path === node.path ||
                     (node.path === '' && selectedFolder?.display_path === node.name);
    const isEmpty = node.file_count === 0 && !hasChildren;
    const isRootFolder = node.name === 'input' || node.path === '';

    return (
      <div key={node.path || 'root'} className="group">
        <Collapsible
          open={isExpanded}
          onOpenChange={() => toggleFolder(node.path)}
        >
          <div
            className={`flex items-center gap-2 p-2 rounded cursor-pointer hover:bg-muted/50 ${
              isSelected ? 'bg-primary/10 border border-primary/20' : ''
            }`}
            style={{ marginLeft: `${level * 16}px` }}
            onClick={() => handleFolderClick(node)}
          >
            {hasChildren && (
              <CollapsibleTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-4 h-4 p-0"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleFolder(node.path);
                  }}
                >
                  {isExpanded ? (
                    <ChevronDown className="w-3 h-3" />
                  ) : (
                    <ChevronRight className="w-3 h-3" />
                  )}
                </Button>
              </CollapsibleTrigger>
            )}

            {!hasChildren && <div className="w-4" />}

            <Folder className="w-4 h-4 text-blue-500" />

            <span className="flex-1 text-sm">
              {node.name === 'input' ? 'Basis-Ordner' : node.name}
            </span>

            {node.file_count > 0 && (
              <Badge variant="secondary" className="text-xs">
                {node.file_count}
              </Badge>
            )}

            <div className="flex gap-1 opacity-0 group-hover:opacity-100">
              <Button
                variant="ghost"
                size="sm"
                className="w-6 h-6 p-0"
                onClick={(e) => {
                  e.stopPropagation();
                  setNewFolderParent(node.path);
                  setCreatingFolder(true);
                }}
                title="Unterordner erstellen"
              >
                <FolderPlus className="w-3 h-3" />
              </Button>

              {isEmpty && !isRootFolder && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-6 h-6 p-0 text-destructive hover:text-destructive"
                  onClick={(e) => {
                    e.stopPropagation();
                    setFolderToDelete(node);
                  }}
                  title="Leeren Ordner löschen"
                >
                  <Trash2 className="w-3 h-3" />
                </Button>
              )}
            </div>
          </div>

          {hasChildren && (
            <CollapsibleContent>
              {node.children.map(child => renderFolderNode(child, level + 1))}
            </CollapsibleContent>
          )}
        </Collapsible>
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className={`space-y-2 ${className}`}>
        <div className="flex items-center gap-2 p-2 rounded animate-pulse">
          <div className="w-4 h-4 bg-muted rounded" />
          <div className="w-16 h-4 bg-muted rounded" />
        </div>
      </div>
    );
  }

  return (
    <div className={`space-y-2 ${className}`}>
      <div className="flex items-center justify-between">
        <Label className="text-sm font-medium">Ziel-Ordner auswählen</Label>
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            setNewFolderParent('');
            setCreatingFolder(true);
          }}
        >
          <FolderPlus className="w-4 h-4 mr-2" />
          Neuer Ordner
        </Button>
      </div>

      <div className="border rounded-md p-2 max-h-64 overflow-y-auto">
        {folderTree && renderFolderNode(folderTree)}
      </div>

      {selectedFolder && (
        <div className="text-sm text-muted-foreground">
          Ausgewählt: <code className="px-1 py-0.5 bg-muted rounded text-xs">
            input/{selectedFolder.display_path}
          </code>
        </div>
      )}

      {creatingFolder && (
        <div className="border rounded-md p-3 space-y-3 bg-muted/20">
          <div className="text-sm font-medium">Neuen Ordner erstellen</div>

          {newFolderParent && (
            <div className="text-xs text-muted-foreground">
              Übergeordnet: input/{newFolderParent}
            </div>
          )}

          <div className="flex gap-2">
            <Input
              placeholder="Ordnername"
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              className="text-sm"
            />
            <Button
              size="sm"
              onClick={handleCreateFolder}
              disabled={!newFolderName.trim() || createFolderMutation.isPending}
            >
              Erstellen
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setCreatingFolder(false)}
            >
              Abbrechen
            </Button>
          </div>
        </div>
      )}

      <AlertDialog open={!!folderToDelete} onOpenChange={() => setFolderToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Ordner löschen?</AlertDialogTitle>
            <AlertDialogDescription>
              Möchten Sie den leeren Ordner <strong>{folderToDelete?.name}</strong> wirklich löschen?
              Diese Aktion kann nicht rückgängig gemacht werden.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setFolderToDelete(null)}>
              Abbrechen
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (folderToDelete) {
                  deleteFolderMutation.mutate(folderToDelete);
                }
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Löschen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};